/**
 * Envelope action buttons (Resend / Correct / Void) for in-flight DocuSign
 * envelopes — shared by the UK & AUS Onboarding Centre panels.
 * "Correct" opens a dialog to fix recipient names/emails and signing order.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Send, Ban, PenSquare, Loader2, Lock, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const EnvelopeActions = ({ envelope, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [correctOpen, setCorrectOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  const envId = envelope.envelope_id;
  const actionable = ["sent", "delivered"].includes(envelope.status);
  if (!actionable) return null;

  const resend = async () => {
    try {
      setBusy("resend");
      await api.post(`/contracts/envelope/${envId}/resend`);
      toast.success("Signing emails resent to pending recipients");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    } finally {
      setBusy(null);
    }
  };

  const doVoid = async () => {
    try {
      setBusy("void");
      await api.post(`/contracts/envelope/${envId}/void`, { reason: voidReason });
      toast.success("Envelope voided");
      setVoidOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Void failed");
    } finally {
      setBusy(null);
    }
  };

  const openCorrect = async () => {
    setCorrectOpen(true);
    setLoadingRecipients(true);
    try {
      const { data } = await api.get(`/contracts/envelope/${envId}/recipients`);
      setRecipients(data.recipients || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load recipients");
      setCorrectOpen(false);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const setR = (i, k, v) => setRecipients((prev) => prev.map((r, ri) => (ri === i ? { ...r, [k]: v } : r)));

  const moveOrder = (i, dir) => {
    setRecipients((prev) => prev.map((r, ri) => {
      if (ri !== i || r.locked) return r;
      const cur = parseInt(r.routing_order || "1", 10);
      return { ...r, routing_order: String(Math.max(1, cur + dir)) };
    }));
  };

  const saveCorrect = async (resendAfter) => {
    const editable = recipients.filter((r) => !r.locked);
    if (editable.some((r) => !r.name && r.type === "signer")) {
      toast.error("Signer names cannot be blank");
      return;
    }
    try {
      setBusy("correct");
      const { data } = await api.put(`/contracts/envelope/${envId}/recipients`, {
        recipients: editable.map((r) => ({
          recipient_id: r.recipient_id, name: r.name, email: r.email,
          routing_order: String(r.routing_order || "1"), type: r.type,
        })),
        resend: resendAfter,
      });
      if (data.success) {
        toast.success(resendAfter ? "Recipients corrected & envelope resent" : "Recipients corrected");
        setCorrectOpen(false);
        onChanged?.();
      } else {
        toast.error(`Some corrections failed: ${(data.errors || []).join("; ")}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Correction failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={resend} disabled={busy === "resend"}
        data-testid={`resend-envelope-${envId}`}>
        {busy === "resend" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
        Resend
      </Button>
      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={openCorrect}
        data-testid={`correct-envelope-${envId}`}>
        <PenSquare className="w-3 h-3 mr-1" /> Correct
      </Button>
      <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
        onClick={() => { setVoidReason(""); setVoidOpen(true); }} data-testid={`void-envelope-${envId}`}>
        <Ban className="w-3 h-3 mr-1" /> Void
      </Button>

      {/* Void dialog */}
      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent className="max-w-md" data-testid="void-envelope-dialog">
          <DialogHeader>
            <DialogTitle>Void Envelope</DialogTitle>
            <DialogDescription>
              This cancels the envelope for all recipients — they can no longer sign. A reason is required
              (recipients see it in the void notification).
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs text-muted-foreground">Void reason *</Label>
            <Input value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Incorrect loan amount — reissuing" data-testid="void-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={doVoid}
              disabled={busy === "void" || !voidReason.trim()} data-testid="confirm-void-btn">
              {busy === "void" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
              Void Envelope
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Correct dialog */}
      <Dialog open={correctOpen} onOpenChange={setCorrectOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="correct-envelope-dialog">
          <DialogHeader>
            <DialogTitle>Correct Envelope — Recipients & Signing Order</DialogTitle>
            <DialogDescription>
              Fix names, emails or the signing order. Recipients who have already signed are locked.
              Lower order numbers sign first; equal numbers sign in parallel.
            </DialogDescription>
          </DialogHeader>
          {loadingRecipients ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600 mr-2" /> Loading recipients…
            </div>
          ) : (
            <div className="space-y-2">
              {recipients.map((r, i) => (
                <div key={r.recipient_id + r.type}
                  className={`border rounded-lg p-2 grid grid-cols-12 gap-2 items-center ${r.locked ? "bg-gray-50 opacity-70" : "bg-white"}`}
                  data-testid={`correct-recipient-${r.recipient_id}`}>
                  <div className="col-span-2 flex items-center gap-1">
                    {r.locked ? <Lock className="w-3 h-3 text-gray-400" /> : (
                      <div className="flex flex-col">
                        <button type="button" onClick={() => moveOrder(i, -1)} className="text-gray-400 hover:text-gray-700">
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button type="button" onClick={() => moveOrder(i, 1)} className="text-gray-400 hover:text-gray-700">
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <Input type="number" min="1" value={r.routing_order || ""} disabled={r.locked}
                      onChange={(e) => setR(i, "routing_order", e.target.value)}
                      className="h-8 w-14 text-center text-sm" data-testid={`recipient-order-${r.recipient_id}`} />
                  </div>
                  <div className="col-span-4">
                    <Input value={r.name} disabled={r.locked} onChange={(e) => setR(i, "name", e.target.value)}
                      placeholder={r.type === "witness" ? "Witness (signer nominates)" : "Name"}
                      className="h-8 text-sm" data-testid={`recipient-name-${r.recipient_id}`} />
                  </div>
                  <div className="col-span-4">
                    <Input value={r.email} disabled={r.locked} onChange={(e) => setR(i, "email", e.target.value)}
                      placeholder="Email" className="h-8 text-sm" data-testid={`recipient-email-${r.recipient_id}`} />
                  </div>
                  <div className="col-span-2 text-xs text-muted-foreground">
                    {r.type === "witness" ? "Witness" : "Signer"}
                    <span className="block">{r.locked ? "✓ signed" : r.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectOpen(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => saveCorrect(false)} disabled={busy === "correct" || loadingRecipients}>
              Save Only
            </Button>
            <Button onClick={() => saveCorrect(true)} disabled={busy === "correct" || loadingRecipients}
              data-testid="save-resend-correct-btn">
              {busy === "correct" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Save & Resend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
